/* global DEBUG_LOG, API_ORIGIN_OVERRIDE */

// mixpanel-specific report utils

import queryString from 'query-string';

import {capitalize, htmlEncodeString, pluralize} from '../../util';
import {
  COUNTRIES,
  NOT_APPLICABLE_PROPERTY_VALUE,
  TRACKING_URL_BY_CLUSTER,
  MAIN_CLUSTER_NAME,
  MappedPropertyValues,
} from '../constants';
import {getEventDisplayName, getPropertyDisplayName} from '../display-name-registry';
import {reporter} from '../error-reporting';
import ironAppsInfo from '../../../apps-info.json';
export {ironAppsInfo};

export type IronAppInfo = {
  appName: string;
  title: string;
  urlEndpoint: string;
};

export const urlToIronAppInfo: {[url: string]: IronAppInfo} = {};
Object.entries(ironAppsInfo).forEach(
  ([appName, appInfo]) => (urlToIronAppInfo[appInfo.urlEndpoint] = {appName, ...appInfo}),
);

/**
 * utils for standalone vs hosted usage
 */

export function isStandalone() {
  return typeof mp === `undefined`;
}

export function getVoltProjectId() {
  const projectIdMatch = /^\/project\/(\d+)\/app\//.exec(window.location.pathname);
  return projectIdMatch && projectIdMatch[1];
}

export function getVoltTab(path = null) {
  const tabMatch = /^\/project\/(\d+)\/app\/([\w-]+)\/?/.exec(path || window.location.pathname);
  return tabMatch && tabMatch[2];
}

/** /report/-format URLs */
export function getLegacyTab(url, {workspaceId, projectId}) {
  const path = workspaceId ? `workspace/${workspaceId}` : `${projectId}`;
  const re = new RegExp(`^/report/${path}/([\\w-]+)`);
  const match = url.match(re);
  return match && match[1];
}

export function isVolt() {
  return !!getVoltProjectId();
}

export function closestReportLink(target) {
  return target.closest(isVolt() ? `a[href^='/project/']` : `a[href^='/report/']`);
}

export function getReportUrl(endpoint, {projectId, workspaceId}) {
  const [, pathname, hash = ``] = endpoint.match(/^([^#]+)(#.*)?$/);
  if (workspaceId) {
    return `/report/workspace/${workspaceId}/${endpoint}`;
  } else if (isVolt() && pathname in urlToIronAppInfo) {
    return `/project/${projectId}/app/${pathname}${hash}`;
  } else {
    return `/report/${projectId}/${endpoint}`;
  }
}

export function apiHost() {
  const envApiOrigin = typeof API_ORIGIN_OVERRIDE !== `undefined` ? API_ORIGIN_OVERRIDE : null;
  if (envApiOrigin) {
    return envApiOrigin;
  }

  if (isStandalone()) {
    // if running standalone on devbox or staging (note: Dashboard app can run standalone in staging), don't override with mixpanel.com
    const devOrigin = /dev(box)?\.mixpanel\.org/.exec(window.location.origin);
    const stagingOrigin = /staging-[\d+]\.mixpanel\.com/.exec(window.location.origin);
    // Need this one so /dashboard-tv, which runs in standalone mode, works for eu.mixpanel.com
    const euOrigin = /eu\.mixpanel\.com/.exec(window.location.origin);
    const urlOptions = queryString.parse(window.location.search);
    return urlOptions[`api_origin`] || (euOrigin || devOrigin || stagingOrigin ? `` : `https://mixpanel.com`);
  }

  return ``;
}

export function getCachedStandaloneAccessToken() {
  if (isStandalone() && typeof localStorage !== `undefined`) {
    const ttlMs = 24 * 60 * 60 * 1000; // one day expiry
    const accessToken = localStorage.getItem(`access_token`);
    const timestamp = localStorage.getItem(`access_token_timestamp`);

    if (accessToken && timestamp && parseInt(timestamp, 10) > Date.now() - ttlMs) {
      return accessToken;
    }
  }

  return null;
}

export function updateStandaloneAccessToken(accessToken) {
  if (isStandalone() && typeof localStorage !== `undefined`) {
    localStorage.setItem(`access_token`, accessToken || ``);
    localStorage.setItem(`access_token_timestamp`, String(Date.now()));
  }
}

/**
 * fetch current project ID, either from:
 * - mp.report.globals (when in hosted context)
 * - URL path (/project/<id>/)
 * - URL query string (when standalone)
 */
export function getProjectID() {
  const voltProjectId = getVoltProjectId();
  if (voltProjectId) {
    // volt mode: /project/42/app/insights
    return voltProjectId;
  } else if (isStandalone()) {
    // standalone mode: ?project_id=42
    return queryString.parse(window.location.search).project_id;
  } else {
    // project ID provided by Django templating
    // @ts-ignore
    return window.mp.report && window.mp.report.globals.project_id;
  }
}

export function getWorkspaceId() {
  if (isStandalone()) {
    // standalone mode: ?workspace_id=42
    return queryString.parse(window.location.search).workspace_id;
  }
  // @ts-ignore
  return window.mp.report && window.mp.report.globals.workspace_id;
}

export function getIsWorkspaceGlobal() {
  if (isStandalone()) {
    // standalone mode: ?is_workspace_global=true
    return queryString.parse(window.location.search).is_workspace_global || false;
  }
  // @ts-ignore
  return window.mp.report && window.mp.report.globals.is_workspace_global;
}

/*
 * downloadData()
 *
 * Initiates a direct download to the browser for any HTTP method,
 * using a terrible hack of submitting a hidden form to a hidden
 * iframe target.
 *
 */
let downloadIdCounter = 1;
export function downloadData(endpoint, params, method = `POST`) {
  // prepare target iframe
  const exportFrameID = `download-frame-${downloadIdCounter++}`;
  const exportFrame = document.createElement(`iframe`);
  exportFrame.id = exportFrameID;
  exportFrame.name = exportFrameID;
  exportFrame.src = ``;
  exportFrame.style.display = `none`;
  document.body.appendChild(exportFrame);

  // prepare source form
  const postForm = document.createElement(`form`);
  postForm.action = endpoint;
  postForm.method = method;
  postForm.style.display = `none`;
  postForm.target = exportFrameID;
  postForm.innerHTML = Object.keys(params)
    .map((p) => `<input type="hidden" name="${p}" value="${htmlEncodeString(params[p])}"/>`)
    .join(``);

  // submit request
  document.body.appendChild(postForm);
  postForm.submit();
}

/**
 * Downloads a file to the user's machine.
 * @param blob - representation of data to be downloaded
 * @param title - title of file to be downloaded
 * @param format - format of file to be downloaded (e.g. PDF/PNG)
 */
export function downloadBlobAsFile(blob: Blob, title: string, format: string) {
  const blobURL = URL.createObjectURL(blob);
  // launch named download via hidden link
  const link = document.createElement(`a`);
  link.style.display = `none`;
  link.href = blobURL;
  link.download = `${title}.${format.toLowerCase()}`;
  document.body.appendChild(link);
  link.click();
  // clean up
  URL.revokeObjectURL(blobURL);
  link.remove();
}

export function showDownloadAlert() {
  const previousDownloadAlertEl = document.querySelector(`mp-alert.download-alert`);
  if (previousDownloadAlertEl) {
    previousDownloadAlertEl.remove();
  }
  const alert = document.createElement(`mp-alert`);
  alert.classList.add(`download-alert`);
  alert.setAttribute(`closeable`, `true`);
  alert.setAttribute(`open`, `true`);
  alert.setAttribute(`large-icon`, `true`);
  alert.innerHTML = `<span>There was an error generating your file. If the issue persists, please <a href="https://help.mixpanel.com/hc/en-us/requests/new" target="_blank">contact support</a>.</span>`;
  alert.addEventListener(`change`, (ev) => {
    // @ts-ignore
    if (ev.detail.state === `closed`) {
      alert.remove();
    }
  });
  document.body.appendChild(alert);
}

export function downloadLocalCSVData(filename, dataStr) {
  // launch named download via hidden link
  const link = document.createElement(`a`);

  const downloadAttrSupported = `download` in link;
  if (downloadAttrSupported) {
    // prepare blob
    const blob = new Blob([dataStr], {type: `text/csv;charset=utf-8`});
    const blobURL = URL.createObjectURL(blob);

    // launch named download via hidden link
    link.style.display = `none`;
    link.href = blobURL;
    link.download = filename;
    document.body.appendChild(link);
    link.click();

    // clean up
    URL.revokeObjectURL(blobURL);
    link.remove();
  } else {
    // Work-around for Safari. One drawback of this method is that it doesn't support setting a filename.
    // On Safari, it downloads the file with the name 'Unknown'
    window.open(`data:attachment/csv;charset=utf-8,` + encodeURIComponent(dataStr));
  }
}

// TODO epurcer - replace this with a more general-purpose tool like https://www.npmjs.com/package/debug
function getLogger(level) {
  if (typeof DEBUG_LOG !== `undefined` && DEBUG_LOG === `true`) {
    /* eslint-disable no-console */
    return function() {
      console[level](...arguments); // eslint-disable-line prefer-rest-params
    };
    /* eslint-enable no-console */
  } else {
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    return function() {};
  }
}

interface Logger {
  [level: string]: Function;
}
export const debug: Logger = [`log`, `info`, `warn`, `error`, `critical`].reduce(
  (ret, level) => ({...ret, [level]: getLogger(level)}),
  {},
);

export function getIconForEvent(_event) {
  return _event.custom ? `custom` : `event`;
}

export function renameEvent(event) {
  return getEventDisplayName(event);
}

const PROPERTY_TYPE_ICON_MAP = {
  boolean: `type-boolean`,
  datetime: `type-date`,
  list: `type-list`,
  number: `type-number`,
  object: `type-object`,
  string: `type-text`,
  dimension: `table`,
};

const PROPERTY_TYPE_LABEL_MAP = {
  boolean: `True / False`,
  datetime: `Date`,
  list: `List`,
  number: `Number`,
  object: `Object`,
  string: `String`,
};

export function getIconForPropertyType(type) {
  return PROPERTY_TYPE_ICON_MAP[type];
}

export function getLabelForPropertyType(type) {
  return PROPERTY_TYPE_LABEL_MAP[type];
}

/*
 * dataGroupId and customProperty are not optional parameters. If the property in question
 * could be a B2B property or custom property and those two parameters are not passed in the
 * property may be improperly renamed.
 */
export function renameProperty(
  propertyName: string | string[],
  {dataGroupId = null, customProperty = null} = {},
): string {
  if (customProperty && customProperty.name) {
    return customProperty.name;
  } else if (Array.isArray(propertyName)) {
    return propertyName.join(`.`);
  } else {
    const renamedProperty = getPropertyDisplayName(propertyName, dataGroupId);
    if (renamedProperty !== propertyName) {
      return renamedProperty;
    }

    // default conversion for all properties starting with '$'
    if (propertyName && propertyName.length > 1 && propertyName.startsWith(`$`)) {
      return propertyName
        .slice(1)
        .split(`_`)
        .map(capitalize)
        .join(` `);
    }
    return propertyName;
  }
}

export function renamePropertyType(type) {
  return (
    {
      number: `Integer`,
      datetime: `Date`,
      boolean: `True/False`,
    }[type] || capitalize(type)
  );
}

export function renameCountryCode(value) {
  return COUNTRIES[String(value).toUpperCase()] || value;
}

export function isBrowserEnvironment() {
  return typeof window !== `undefined`;
}

/**
 * Provides explicit configuration of global mapping state for the renamePropertyValue function. This allows
 * callers of renamePropertyValue to retrieve mapped properties values without access to the configurations.
 */
export function updatePropertyValueMappings(propertyValueMappings) {
  if (!isBrowserEnvironment()) {
    return;
  }
  // TODO(RNA-230): Remove dependency on mappings being global by refactoring callsites to pass the mappings to
  // renamePropertyValue.
  // @ts-ignore
  window.__propertyValueMappings = propertyValueMappings;
}

/**
 * Provides the list of global mappings
 */
export function getPropertyValueMappings() {
  if (!isBrowserEnvironment()) {
    return null;
  }
  // @ts-ignore
  return window.__propertyValueMappings;
}

export function renamePropertyValue(value, propertyName) {
  if (propertyName === `$event`) {
    // Value is an event
    return renameEvent(value);
  } else if (propertyName === `$people`) {
    // Value is a property
    return renameProperty(value); // value is $all_people
  }

  // Value is a property value

  if (value === NOT_APPLICABLE_PROPERTY_VALUE) {
    return `Not Applicable`;
  } else if (propertyName && propertyName.includes(`country_code`)) {
    return renameCountryCode(value);
  }

  const id = parseInt(value, 10);
  const propertyValueMappings = getPropertyValueMappings();
  if (!propertyValueMappings || Number.isNaN(id)) {
    return value;
  }

  let mappedPropertyValue = value;
  const {projectCampaigns = {}, campaignVariants = [], experiments = [], datasets = {}} = propertyValueMappings;
  switch (propertyName) {
    case MappedPropertyValues.Campaigns:
    case MappedPropertyValues.CampaignId:
      // map message IDs to their names
      mappedPropertyValue = projectCampaigns[id] || value;
      break;
    case MappedPropertyValues.MessageId: {
      // map message variant IDs to their numbers and campaign
      const campaign = campaignVariants.find((c) => c.variants.includes(id));
      if (campaign) {
        const variantIdx = campaign.variants.indexOf(id);
        mappedPropertyValue =
          variantIdx === 0 ? `${campaign.name} - Original variant` : `${campaign.name} - Variant ${variantIdx}`;
      }
      break;
    }
    case MappedPropertyValues.ExperimentId: {
      // map experiment IDs to their names
      const experiment = experiments.find((exp) => exp.id === id);
      if (experiment) {
        mappedPropertyValue = experiment.name;
      }
      break;
    }
    case MappedPropertyValues.VariantId: {
      // map experiment variant IDs to their numbers and experiment
      const experiment = experiments.find((exp) => exp.variants.some((variant) => variant.id === id));
      if (experiment) {
        const variantIdx = experiment.variants
          .map((variant) => variant.id)
          .sort((a, b) => a - b)
          .indexOf(id);
        mappedPropertyValue =
          variantIdx === 0 ? `${experiment.name} - Original variant` : `${experiment.name} - Variant ${variantIdx}`;
      }
      break;
    }
    case MappedPropertyValues.Dataset: {
      const dataset = datasets[value];
      if (dataset) {
        mappedPropertyValue = `${dataset.name} (id: ${dataset.projectId})`;
      }
      break;
    }
    default:
      break;
  }

  return mappedPropertyValue;
}

export function formatMaxHistoryDays(maxHistoryDays) {
  if (maxHistoryDays < 365) {
    return `${maxHistoryDays} days`;
  } else {
    const years = Math.floor(maxHistoryDays / 365);
    return `${years} ${pluralize(`year`, years)}`;
  }
}

export function getBaseTrackingUrl(clusterName) {
  let endpoint = TRACKING_URL_BY_CLUSTER[MAIN_CLUSTER_NAME];
  if (!Object.keys(TRACKING_URL_BY_CLUSTER).includes(clusterName)) {
    // n.b.: mp.report.globals.project_cluster defaults to an empty string if it is not included by the view
    reporter.error(
      new Error(
        `mp.report.globals.project_cluster (${clusterName}) not in ${Object.keys(TRACKING_URL_BY_CLUSTER)}. ` +
          `Current URL: ${window.location.href}`,
      ),
    );
  } else {
    endpoint = TRACKING_URL_BY_CLUSTER[clusterName];
  }

  return endpoint;
}

export function projectOnPy3(projectId: number | string) {
  return Number(projectId) % 100 < 40;
}

export function py3ProjectHeaders(projectId) {
  return projectOnPy3(projectId)
    ? {
        'x-query-api-python': `python3`,
      }
    : {};
}
